<script setup lang="ts" generic="T extends Record<string, any>">
import { computed } from 'vue'

import { renderer } from '@fondof-ui/components/content-builder/render'
import type { ComponentMap } from '@fondof-ui/components/content-builder/types'
import { MediaImage, MediaRoot } from '@fondof-ui/components/media'
import type { Media } from '@fondof-ui/components/media/types'
import Log from '@shared/fondof-fn/log/Log.vue'

const props = defineProps<{
  collection: T
  componentMap: ComponentMap
  backgroundColor?: string
  backgroundImage?: Media
  specialClass?: string
}>()

const filteredCollection = computed(() =>
  props.collection.filter((item: any) => item && Object.keys(item).length > 0)
)

const normalizeHexColor = (color: string | number): string | null => {
  if (color === null || color === undefined) return null

  let colorString = String(color).trim()

  if (!colorString.startsWith('#')) {
    colorString = `#${colorString}`
  }

  if (colorString.length === 4 || colorString.length === 7) {
    return colorString
  }

  return null
}

const backgroundStyle = computed(() => {
  if (props.specialClass?.trim() === 'background--gradient--midnight-edition') {
    return {
      style: {
        backgroundImage:
          'linear-gradient(180deg, #7a98fc, #bf4a3f 38%, #d5604b 66%, #d7afcb)',
        opacity: 1,
      },
    }
  }

  const validBackgroundColor = normalizeHexColor(props.backgroundColor || '')

  if (!validBackgroundColor) {
    return
  }

  return {
    style: {
      backgroundColor: validBackgroundColor,
    },
  }
})
</script>

<template>
  <div
    v-bind="backgroundStyle"
    class="relative"
  >
    <MediaRoot
      v-if="props.backgroundImage"
      class="z-background-image absolute left-0 top-0 size-full"
      :entry="props.backgroundImage"
    >
      <template #image="{ data }">
        <MediaImage
          v-bind="data"
          cover-image
          class="pointer-events-none"
          alt=""
        />
      </template>
    </MediaRoot>
    <template
      v-for="(item, index) in filteredCollection"
      :key="index"
    >
      <component
        :is="componentMap['ContentCollection']"
        v-if="item.__typename === 'ContentCollection'"
        :component-map="props.componentMap"
        :background-color="item.backgroundColor"
        :background-image="item.backgroundImage"
        :special-class="item.specialClass"
        :collection="item.contentItemsCollection.items"
        class="relative overflow-hidden"
      />
      <component
        :is="renderer(item, props.componentMap)"
        v-else-if="renderer(item, props.componentMap)"
        v-bind="item"
      />
      <Log
        v-else
        level="warn"
        :message="`Component of type ${item.__typename} not found in map.`"
      />
    </template>
  </div>
</template>
